// Secure configuration with environment variables
// When running in environments outside of Vite (like Node tests),
// `import.meta.env` may be undefined. Fall back to `process.env` in those cases.
const env = (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env : process.env || {};
const isDevelopment = env.DEV;
const isDebugEnabled = env.VITE_ENABLE_DEBUG === 'true';

// Check if GTM should be enabled in development
// You can control this with VITE_GTM_ENABLE_IN_DEV environment variable
const enableGTMInDev = env.VITE_GTM_ENABLE_IN_DEV === 'true';

// Validate required environment variables
const requiredEnvVars = [
  'VITE_RECAPTCHA_SITE_KEY',
  'VITE_GOOGLE_SCRIPT_URL',
  'VITE_GTM_ID'
];

requiredEnvVars.forEach(varName => {
  if (!env[varName]) {
    console.warn(`Missing environment variable: ${varName}`);
  }
});

// Configuration object with fallbacks for development
export const config = {
  recaptcha: {
    siteKey: env.VITE_RECAPTCHA_SITE_KEY || '',
    enabled: !!env.VITE_RECAPTCHA_SITE_KEY
  },
  
  googleScript: {
    url: env.VITE_GOOGLE_SCRIPT_URL || '',
    timeout: 30000, // 30 seconds
    retryAttempts: 3
  },

  gtm: {
    id: env.VITE_GTM_ID || '',
    // Enable GTM if ID exists and either:
    // 1. We're in production, OR
    // 2. We're in development and explicitly enabled it
    enabled: !!env.VITE_GTM_ID && (!isDevelopment || enableGTMInDev)
  },

  analytics: {
    enabled: !!env.VITE_GTM_ID && (!isDevelopment || enableGTMInDev),
    debugMode: isDevelopment && isDebugEnabled
  },

  security: {
    allowedDomains: (env.VITE_ALLOWED_DOMAINS || '').split(',').filter(Boolean),
    enableDebug: isDevelopment && isDebugEnabled,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    allowedMimeTypes: new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp'])
  },
  
  api: {
    endpoint: env.VITE_API_ENDPOINT || '/api',
    key: env.VITE_API_KEY || ''
  }
};

// Log GTM status for debugging
if (isDevelopment) {
  console.log('GTM Configuration:', {
    id: config.gtm.id ? `${config.gtm.id.substring(0, 4)}...` : 'Not set',
    enabled: config.gtm.enabled,
    isDevelopment,
    enableGTMInDev
  });
}

// Secure logging function
export const secureLog = (...args) => {
  if (config.security.enableDebug) {
    console.log('[DEBUG]', ...args);
  }
};

// Validate file type
export const isValidFileType = (file) => {
  if (!file || !file.type) return false;
  return config.security.allowedMimeTypes.has(file.type.toLowerCase());
};

// Validate file size
export const isValidFileSize = (file) => {
  if (!file || !file.size) return false;
  return file.size <= config.security.maxFileSize;
};

// Sanitize user input
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  
  // Remove any script tags or dangerous HTML
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
};

// Validate email
export const isValidEmail = (email) => {
  if (!email) return false;
  // Simpler but effective email regex that handles most common cases
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
};

// Validate phone number (Israeli format)
export const isValidPhone = (phone) => {
  if (!phone) return false;
  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, '');
  // Israeli phone: 9-10 digits, starting with 0
  return /^0[1-9]\d{7,8}$/.test(cleaned);
};

// Format phone number for display
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 5)}-${cleaned.slice(5)}`;
  } else if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

// Validate URL
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Get safe error message (hide sensitive details)
export const getSafeErrorMessage = (error) => {
  if (config.security.enableDebug) {
    return error.message || 'An error occurred';
  }
  // In production, return generic message
  return 'An error occurred. Please try again later.';
};

// Check if domain is allowed
export const isDomainAllowed = (domain) => {
  if (!config.security.allowedDomains.length) return true;
  return config.security.allowedDomains.includes(domain);
};

// Rate limiting helper
const rateLimitMap = new Map();
export const checkRateLimit = (key, maxAttempts = 5, windowMs = 60000) => {
  const now = Date.now();
  const attempts = rateLimitMap.get(key) || [];
  
  // Remove old attempts outside the window
  const validAttempts = attempts.filter(timestamp => now - timestamp < windowMs);
  
  if (validAttempts.length >= maxAttempts) {
    return false; // Rate limit exceeded
  }
  
  validAttempts.push(now);
  rateLimitMap.set(key, validAttempts);
  return true;
};

// Export all validators
export const validators = {
  isValidEmail,
  isValidPhone,
  isValidUrl,
  isValidFileType,
  isValidFileSize
};

// Export all formatters
export const formatters = {
  formatPhone,
  sanitizeInput
};

// Export all security helpers
export const security = {
  getSafeErrorMessage,
  isDomainAllowed,
  checkRateLimit
};